import { useState } from 'react'

import { GridOptions } from 'ag-grid-community'
import cx from 'classnames'
import { includes } from 'lodash'

import {
  sectionColDefByIdFunc,
  sectionColGroupDefByIdFunc,
} from '../sectionColumnsDef'
import { CPModel, ReportVariant } from '@ors/types/variants.ts'

function useGridOptions(props: { variant: ReportVariant }) {
  const { variant } = props
  const sectionColDefById = sectionColDefByIdFunc(variant)
  const sectionColGroupDefById = sectionColGroupDefByIdFunc(variant)
  const [gridOptions] = useState<GridOptions>({
    columnDefs: [
      {
        cellClass: 'bg-mui-box-background',
        cellRendererParams: (props: any) => ({
          className: cx({
            'font-bold': includes(
              ['group', 'total', 'subtotal'],
              props.data.rowType,
            ),
          }),
        }),
        field: 'facility',
        headerClass: 'ag-text-left',
        headerName: 'Facility name or identifier',
        ...sectionColDefById['facility'],
      },
      {
        dataType: 'number',
        field: 'total',
        headerName: variant.match([CPModel.VI])
          ? 'Total amount generated (tonnes)'
          : 'Total amount generated',
        orsAggFunc: 'sumTotal',
        ...sectionColDefById['total_amount_generated'],
      },
      ...(variant.match([CPModel.VI])
        ? [
            {
              dataType: 'number',
              field: 'stored_at_start_of_year',
              headerName: 'Amount stored at the beginning of the year (tonnes)',
              orsAggFunc: 'sumTotal',
              ...sectionColDefById['stored_at_start_of_year'],
            },
          ]
        : []),
      {
        children: [
          {
            dataType: 'number',
            field: 'all_uses',
            headerName: 'For uses excluding feedstocks',
            orsAggFunc: 'sumTotal',
            ...sectionColDefById['all_uses'],
          },
          {
            dataType: 'number',
            field: 'feedstock_gc',
            headerName: variant.match([CPModel.VI])
              ? 'For feedstock use in your country'
              : 'For all uses',
            orsAggFunc: 'sumTotal',
            ...sectionColDefById['feedstock_gc'],
          },
          {
            dataType: 'number',
            field: 'destruction',
            headerName: 'For destruction',
            orsAggFunc: 'sumTotal',
            ...sectionColDefById['destruction'],
          },
        ],
        groupId: 'amount_generated_and_captured',
        headerGroupComponent: 'agColumnHeaderGroup',
        headerName: variant.match([CPModel.VI])
          ? 'Amount generated and captured (tonnes)'
          : 'Amount generated and captured',
        marryChildren: true,
        ...sectionColGroupDefById['amount_generated_and_captured'],
      },
      {
        dataType: 'number',
        field: 'feedstock_wpc',
        headerName: variant.match([CPModel.VI])
          ? 'Amount used for feedstock without prior capture (tonnes)'
          : 'Amount used for feedstock without prior capture',
        orsAggFunc: 'sumTotal',
        ...sectionColDefById['feedstock_wpc'],
      },
      {
        dataType: 'number',
        field: 'destruction_wpc',
        headerName: variant.match([CPModel.VI])
          ? 'Amount destroyed in the facility without prior capture (tonnes)'
          : 'Amount destroyed without prior capture',
        orsAggFunc: 'sumTotal',
        ...sectionColDefById['destruction_wpc'],
      },
      {
        dataType: 'number',
        field: 'generated_emissions',
        headerName: variant.match([CPModel.VI])
          ? 'Amount of generated emissions (tonnes)'
          : 'Amount of generated emissions',
        orsAggFunc: 'sumTotal',
        ...sectionColDefById['generated_emissions'],
      },
      ...(variant.match([CPModel.VI])
        ? [
            {
              cellDataType: 'number',
              cellEditor: 'agNumberCellEditor',
              dataType: 'number',
              field: 'stored_at_end_of_year',
              headerName: 'Amount stored at the end of the year (tonnes)',
              orsAggFunc: 'sumTotal',
              ...sectionColDefById['stored_at_end_of_year'],
            },
          ]
        : []),
      {
        field: 'remarks',
        headerName: 'Remarks',
        ...sectionColDefById['remarks'],
      },
    ],
    defaultColDef: {
      autoHeight: true,
      cellClass: 'ag-text-right',
      headerClass: 'ag-text-center',
      // minWidth: defaultColDef.minWidth,
      resizable: true,
      wrapText: true,
    },
  })

  return gridOptions
}

export default useGridOptions
